/**
 * Tree converter utilities for converting between backend tree and TipTap JSON.
 */

import type { TreeNode, NodeMark, PatchOp } from "./types";
import type { JSONContent } from "@tiptap/react";

// =============================================================================
// Backend Tree → TipTap JSON
// =============================================================================

/**
 * Convert backend tree to TipTap JSONContent
 */
export function treeToTiptap(tree: TreeNode): JSONContent {
  if (!tree) {
    return { type: "doc", content: [] };
  }

  const doc: JSONContent = {
    type: "doc",
    content: [],
  };

  // Flatten sections into doc content
  const sections = tree.children || [];

  for (const section of sections) {
    if (section.type === "section") {
      const sectionContent = flattenSection(section);
      doc.content!.push(...sectionContent);
    }
  }

  return doc;
}

/**
 * Flatten a section node into TipTap content
 * Section becomes a heading + its content
 */
function flattenSection(section: TreeNode): JSONContent[] {
  const content: JSONContent[] = [];

  // Section itself becomes a heading (preserve level, default to h2)
  const heading: JSONContent = {
    type: "heading",
    attrs: {
      level: (section.attrs?.headingLevel as number) || 2,
      nodeId: section.id,
      locked: section.attrs?.locked || false,
    },
    content: [{ type: "text", text: (section.attrs?.title as string) || "" }],
  };
  content.push(heading);

  // Add section's children (bulletList, paragraph, etc.)
  const children = section.children || [];
  for (const child of children) {
    const converted = nodeToTiptap(child);
    if (converted) {
      content.push(converted);
    }
  }

  // Add empty paragraph for cursor placement if section has no content
  if (children.length === 0) {
    content.push({
      type: "paragraph",
      attrs: { nodeId: `${section.id}_empty` },
      content: [],
    });
  }

  return content;
}

/**
 * Parse markdown bold (**text**) into TipTap text nodes with marks.
 * Returns an array of text nodes.
 */
function parseMarkdownBold(
  text: string,
  existingMarks: NodeMark[] = []
): JSONContent[] | null {
  const nodes: JSONContent[] = [];
  const regex = /\*\*([^*]+)\*\*/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    // Add text before the match (if any)
    if (match.index > lastIndex) {
      const beforeText = text.slice(lastIndex, match.index);
      if (beforeText) {
        const node: JSONContent = { type: "text", text: beforeText };
        if (existingMarks.length > 0) {
          node.marks = [...existingMarks];
        }
        nodes.push(node);
      }
    }

    // Add the bold text
    const boldText = match[1];
    if (boldText) {
      nodes.push({
        type: "text",
        text: boldText,
        marks: [{ type: "bold" }, ...existingMarks],
      });
    }

    lastIndex = regex.lastIndex;
  }

  // Add remaining text after last match
  if (lastIndex < text.length) {
    const afterText = text.slice(lastIndex);
    if (afterText) {
      const node: JSONContent = { type: "text", text: afterText };
      if (existingMarks.length > 0) {
        node.marks = [...existingMarks];
      }
      nodes.push(node);
    }
  }

  // If no matches found, return null to use default handling
  if (nodes.length === 0) {
    return null;
  }

  return nodes;
}

/** Parse inline links before bold so URL contents are never treated as formatting. */
function parseMarkdownInline(text: string, marks: NodeMark[] = []): JSONContent[] {
  // Native links have already been parsed (e.g. after saving and reloading).
  if (marks.some((mark) => mark.type === "link")) {
    return [{ type: "text", text, marks }];
  }
  const nodes: JSONContent[] = [];
  const appendText = (value: string) => {
    if (value) nodes.push(...(parseMarkdownBold(value, marks) || []));
  };
  // Support parentheses in article URLs as well as ordinary Markdown destinations.
  const links = /\[([^\]\n]+)\]\(([^()\s]*(?:\([^()\s]*\)[^()\s]*)*)\)/g;
  let lastIndex = 0;
  for (const match of text.matchAll(links)) {
    const href = match[2];
    try {
      const url = new URL(href);
      if (!/^https?:\/\//i.test(href) || !["http:", "https:"].includes(url.protocol) || !url.hostname) continue;
    } catch {
      continue; // Unsupported/malformed destinations remain literal text.
    }
    appendText(text.slice(lastIndex, match.index));
    nodes.push({
      type: "text",
      text: match[1],
      marks: [...marks, { type: "link", attrs: { href, target: "_blank", rel: "noopener noreferrer" } }],
    });
    lastIndex = match.index! + match[0].length;
  }
  appendText(text.slice(lastIndex));
  return nodes;
}

/**
 * Convert a single node to TipTap format
 * UniqueID extension handles nodeId for block nodes, we use marks for text nodes
 */
function nodeToTiptap(node: TreeNode): JSONContent | JSONContent[] | null {
  switch (node.type) {
    case "heading":
      return {
        type: "heading",
        attrs: {
          level: (node.attrs?.level as number) || 2,
          nodeId: node.id,
          locked: node.attrs?.locked || false,
        },
        content: (node.children || [])
          .map(nodeToTiptap)
          .filter(Boolean)
          .flat() as JSONContent[],
      };

    case "bulletList":
      return {
        type: "bulletList",
        attrs: { nodeId: node.id },
        content: (node.children || [])
          .map(nodeToTiptap)
          .filter(Boolean)
          .flat() as JSONContent[],
      };

    case "listItem":
      return {
        type: "listItem",
        attrs: {
          nodeId: node.id,
          locked: node.attrs?.locked || false,
        },
        content: (node.children || [])
          .map(nodeToTiptap)
          .filter(Boolean)
          .flat() as JSONContent[],
      };

    case "paragraph": {
      // Process children, handling text nodes that may expand to multiple nodes
      const paragraphContent: JSONContent[] = [];
      for (const child of node.children || []) {
        const result = nodeToTiptap(child);
        if (result) {
          // If result is an array (from parseMarkdownBold), spread it
          if (Array.isArray(result)) {
            paragraphContent.push(...result);
          } else {
            paragraphContent.push(result);
          }
        }
      }
      return {
        type: "paragraph",
        attrs: { nodeId: node.id },
        content: paragraphContent,
      };
    }

    case "text": {
      const text = node.text || "";
      return parseMarkdownInline(text, node.marks || []);
    }

    default:
      return null;
  }
}

// =============================================================================
// TipTap JSON → Backend Tree
// =============================================================================

/**
 * Convert TipTap JSONContent back to tree format for sync
 */
export function tiptapToTree(
  doc: JSONContent,
  existingTree: TreeNode | null = null
): TreeNode {
  const root: TreeNode = {
    id: "doc_root",
    type: "doc",
    author: "agent",
    children: [],
  };

  // Build lookup map from existing tree for ID reuse
  const existingNodes = existingTree ? buildNodeMap(existingTree) : new Map();

  let currentSection: TreeNode | null = null;

  for (const node of doc.content || []) {
    const headingLevel = node.attrs?.level as number | undefined;
    if (node.type === "heading" && (headingLevel === 1 || headingLevel === 2)) {
      // h1/h2 heading becomes a section
      const title = extractTextFromTiptap(node);
      const sectionId = slugify(title);

      // Use nodeId from heading if available (preserves section_xxx ID)
      const sectionNodeId =
        (node.attrs?.nodeId as string) || `section_${sectionId}`;

      // Try to reuse existing section
      const existingSection = existingNodes.get(sectionNodeId);

      currentSection = {
        id: sectionNodeId,
        type: "section",
        attrs: {
          sectionId,
          title,
          headingLevel,
          locked:
            (node.attrs?.locked as boolean) ||
            (existingSection?.attrs?.locked as boolean) ||
            false,
        },
        author: existingSection?.author || "agent",
        children: [], // Content only, no heading child
      };
      root.children!.push(currentSection);
    } else if (currentSection && node.type) {
      // Skip empty paragraphs (they're visual-only, shouldn't sync to backend)
      if (
        node.type === "paragraph" &&
        (!node.content || node.content.length === 0)
      ) {
        continue;
      }
      const treeNode = tiptapNodeToTree(node, existingNodes);
      if (treeNode) {
        currentSection.children!.push(treeNode);
      }
    }
  }

  return root;
}

/**
 * Build a map of all nodes by ID for quick lookup
 */
function buildNodeMap(
  node: TreeNode,
  map: Map<string, TreeNode> = new Map()
): Map<string, TreeNode> {
  if (node.id) {
    map.set(node.id, node);
  }
  for (const child of node.children || []) {
    buildNodeMap(child, map);
  }
  return map;
}

/**
 * Convert a TipTap node to backend tree node
 */
function tiptapNodeToTree(
  node: JSONContent,
  existingNodes: Map<string, TreeNode> = new Map()
): TreeNode {
  // Get node ID from attrs (UniqueID handles block nodes)
  const nodeId =
    (node.attrs?.nodeId as string) || generateId(node.type || "node");

  // Look up existing node by ID to get author
  const existingNode = existingNodes.get(nodeId);

  const treeNode: TreeNode = {
    id: nodeId,
    type: (node.type || "unknown") as TreeNode["type"],
    author: existingNode?.author || "agent",
  };

  if (node.attrs) {
    // Copy attrs but exclude our metadata fields (they're stored at top level)
    const { nodeId: _nodeId, nodeAuthor: _nodeAuthor, ...restAttrs } = node.attrs;
    if (Object.keys(restAttrs).length > 0) {
      // Merge with existing attrs to preserve locked state
      treeNode.attrs = { ...existingNode?.attrs, ...restAttrs };
    } else if (existingNode?.attrs) {
      // Preserve existing attrs (like locked) even if no new attrs
      treeNode.attrs = existingNode.attrs;
    }
  } else if (existingNode?.attrs) {
    // No attrs in TipTap node but existing node has attrs - preserve them
    treeNode.attrs = existingNode.attrs;
  }

  if (node.text !== undefined) {
    treeNode.text = node.text;
  }

  // Preserve marks for text nodes (e.g., bold)
  if (node.marks && node.marks.length > 0) {
    treeNode.marks = node.marks as NodeMark[];
  }

  if (node.content) {
    treeNode.children = node.content.map((child, index) => {
      const childNode = tiptapNodeToTree(child, existingNodes);
      // For inline nodes (text, hardBreak) without UniqueID, derive ID from parent + position
      if (!child.attrs?.nodeId) {
        childNode.id = `${nodeId}_${child.type}_${index}`;
      }
      return childNode;
    });
  }

  return treeNode;
}

/**
 * Extract text from a TipTap node
 */
function extractTextFromTiptap(node: JSONContent): string {
  if (node.text) return node.text;
  if (node.content) {
    return node.content.map(extractTextFromTiptap).join("");
  }
  return "";
}

/**
 * Convert text to snake_case ID
 */
function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/[-\s]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

/**
 * Generate a unique ID
 */
function generateId(prefix: string): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}

// =============================================================================
// Patch Application
// =============================================================================

/**
 * Apply a patch operation to the local tree state
 */
export function applyPatch(tree: TreeNode, patch: PatchOp): TreeNode {
  // Deep clone the tree
  const newTree = JSON.parse(JSON.stringify(tree)) as TreeNode;

  switch (patch.op) {
    case "node.add":
      if (patch.parentId && patch.node) {
        const parent = findNode(newTree, patch.parentId);
        if (parent) {
          if (!parent.children) parent.children = [];
          if (patch.position !== undefined && patch.position !== null) {
            parent.children.splice(patch.position, 0, patch.node);
          } else {
            parent.children.push(patch.node);
          }
        } else {
          console.warn("Parent not found for patch:", patch.parentId);
        }
      }
      break;

    case "node.update":
      if (patch.nodeId && patch.updates) {
        const node = findNode(newTree, patch.nodeId);
        if (node) {
          if (patch.updates.text !== undefined) {
            node.text = patch.updates.text;
          }
          if (patch.updates.attrs) {
            node.attrs = { ...node.attrs, ...patch.updates.attrs };
          }
          if (patch.updates.author) {
            node.author = patch.updates.author;
          }
          if (patch.updates.children !== undefined) {
            node.children = patch.updates.children;
          }
        }
      }
      break;

    case "node.delete":
      if (patch.nodeId) {
        deleteNode(newTree, patch.nodeId);
      }
      break;

    case "status.update":
      // Status updates don't modify the tree structure
      break;
  }

  return newTree;
}

/**
 * Apply multiple patches to a tree.
 */
export function applyPatches(tree: TreeNode, patches: PatchOp[]): TreeNode {
  let result = tree;
  for (const patch of patches) {
    result = applyPatch(result, patch);
  }
  return result;
}

/**
 * Find a node by ID in the tree
 */
export function findNode(root: TreeNode, id: string): TreeNode | null {
  if (root.id === id) return root;
  for (const child of root.children || []) {
    const found = findNode(child, id);
    if (found) return found;
  }
  return null;
}

/**
 * Delete a node from the tree
 */
function deleteNode(root: TreeNode, id: string): boolean {
  if (!root.children) return false;
  const index = root.children.findIndex((c) => c.id === id);
  if (index !== -1) {
    root.children.splice(index, 1);
    return true;
  }
  for (const child of root.children) {
    if (deleteNode(child, id)) return true;
  }
  return false;
}

/**
 * Extract plain text from a tree node.
 */
export function extractText(node: TreeNode): string {
  if (node.type === "text" && node.text) {
    return node.text;
  }
  if (node.type === "hardBreak") {
    return " ";
  }
  if (node.children) {
    return node.children.map(extractText).join("");
  }
  return "";
}
